/**
 * The event envelope.
 *
 * Every fact in this system — a sale, a stock movement, a payroll run — is an immutable
 * event in an append-only log. Three things fall out of that single decision, and they are
 * the reason the architecture looks like this:
 *
 *   1. Offline sync stops having conflicts. Append-only facts do not fight. Two devices
 *      selling the last unit both produce valid events; stock goes negative and surfaces
 *      for review. No sale is ever lost to a merge.
 *   2. Double-entry becomes a projection of the same log, so the books cannot silently
 *      disagree with operations.
 *   3. The audit trail is not a feature. It is the log.
 *
 * Current stock, weighted-average cost and account balances are all derived, which also
 * means a costing bug is fixed by replaying the log rather than by hand-patching balances.
 */

import { z } from 'zod';

export const envelopeSchema = z.object({
  /** Client-generated UUIDv7. Doubles as the idempotency key, so a retry over a flaky link cannot double-post. */
  id: z.string().uuid(),

  type: z.string().min(1),
  payload: z.unknown(),

  /** Which physical device produced this. */
  deviceId: z.string().uuid(),
  /** Monotonic per device. Gaps mean events were lost and are worth shouting about. */
  deviceSeq: z.number().int().positive(),

  /** The signed-in user. Never optional: an unattributed event is a useless audit record. */
  actorId: z.string().uuid(),

  /** Device wall clock at creation. May be wrong; see serverTs. */
  clientTs: z.string().datetime({ offset: true }),
  /** Device clock offset from server time, in ms, as last measured. Records how wrong clientTs may be. */
  clockOffsetMs: z.number().int().default(0),
});

export type Envelope = z.infer<typeof envelopeSchema>;

/**
 * An envelope after the server has accepted it.
 *
 * serverSeq is the single global ordering authority. Devices propose order; the server
 * decides it. That is what makes last-write-wins deterministic across devices that have
 * never seen each other.
 */
export const storedEnvelopeSchema = envelopeSchema.extend({
  serverSeq: z.number().int().positive(),
  serverTs: z.string().datetime({ offset: true }),
});

export type StoredEnvelope = z.infer<typeof storedEnvelopeSchema>;

export function isStored(e: Envelope | StoredEnvelope): e is StoredEnvelope {
  return typeof (e as StoredEnvelope).serverSeq === 'number';
}

/**
 * Effective ordering key for last-write-wins on mutable entities.
 *
 * Synced events order by the server's sequence. Local events not yet acknowledged sort
 * after everything the server has seen, so a cashier's own un-synced edit still wins on
 * their screen — and is re-ordered honestly once the server assigns it a real position.
 */
export function orderKey(e: Envelope | StoredEnvelope): number {
  return isStored(e) ? e.serverSeq : Number.MAX_SAFE_INTEGER;
}
